import 'C:\\Projects\\genkei\\zumeco\\static\\folder\\FolderList.tsx.esbuild-plugin-windicss.css';
import { cx } from '@linaria/core';
import { mdiBellOutline, mdiDotsVertical, mdiFolder, mdiFolderHome, mdiFolderPlus, mdiMagnify } from '@mdi/js';
import { memo } from 'preact/compat';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Folder, QueryWhere } from '../../server/src/models';
import { URL_API_CURRENT_USER_VISITED_NOTICE, URL_FOLDER } from '../../server/src/urls';
import { COLOR_PANEL_SHADOW } from '../shared/colors';
import { Dropdown } from '../shared/components/Dropdown';
import { DropdownMenu } from '../shared/components/DropdownMenu';
import { Icon } from '../shared/components/Icon';
import { i18n } from '../shared/i18n';
import { withProgressBar } from '../shared/utils';
import { openChangeFolderOwnerDialog } from './ChangeOwnerDialog';
import { Collapsible } from './Collapsible';
import { FolderButton } from './FolderButton';
import { useCurrentUser, useCurrentUserTenant } from './data/account';
import { createFolder, deleteFolder, renameFolder, usePersonalFolders, useSharedFolders, useTenantFolders } from './data/folders';
import { onDragEnterOnFolder, onDragLeaveOnFolder, onDragOverOnFolder, onDropOnFolder } from './dropLink';
import { ENABLE_HANDOVER, getCurrentDoodlesQuery, setCurrentDoodlesQuery, useCurrentDoodlesQuery } from './folderState';
const FolderListItem = memo(({
  folder,
  active
}: {
  folder: Folder;
  active: boolean;
}) => {
  const [isOpen, setOpen] = useState(false);
  const items = useMemo(() => [{
    label: i18n.rename,
    onClick: () => renameFolder(folder.id, folder.name)
  }, ...(!ENABLE_HANDOVER || folder.tenantSharing ? [] : [{
    label: i18n.handover.handover,
    onClick: () => openChangeFolderOwnerDialog(folder)
  }]), {
    label: i18n.delete,
    onClick: () => withProgressBar(async () => {
      await deleteFolder(folder.id);
      const query = getCurrentDoodlesQuery();
      query.kind === 'folder' && query.folderId === folder.id && (await setCurrentDoodlesQuery({
        kind: 'folder',
        folderId: ''
      }));
    })
  }], [folder]);
  return <li>
      <div class="relative flex items-center mr-1">
        <a data-folder-id={folder.id} href={URL_FOLDER(folder.id)} class={cx('flex-auto pl-4 pr-2.5em h-2.2em flex items-center hover:text-hex-$primary-color', active && 'text-hex-$primary-color')} onDragOver={onDragOverOnFolder} onDragEnter={onDragEnterOnFolder} onDragLeave={onDragLeaveOnFolder} onDrop={onDropOnFolder}>
          <Icon icon={folder.tenantSharing ? mdiFolderHome : mdiFolder} className="mr-1 -mt-1px" />
          {folder.name}
        </a>
        {folder.id && <div class="absolute inset-0 left-auto mr-1 flex items-center">
            <Dropdown isOpen={isOpen} setOpen={setOpen} alignment="right" caret={false} buttonClass="h-2em w-2em !rounded-full" dropdownClass="z-1">
              <Icon icon={mdiDotsVertical} />
              <DropdownMenu items={items} setOpen={setOpen} />
            </Dropdown>
          </div>}
      </div>
    </li>;
});
const prettyBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${i18n.formatNumber(Math.ceil(bytes / (1024 * 1024 * 102.4)) / 10)} GB`;
  } else if (bytes >= 1024 * 1024 * 10) {
    return `${i18n.formatNumber(Math.ceil(bytes / (1024 * 1024)))} MB`;
  } else {
    return `${i18n.formatNumber(Math.ceil(bytes / (1024 * 102.4)) / 10)} MB`;
  }
};
const StorageUsage = memo(({
  usage,
  limit
}: {
  usage: number;
  limit: number;
}) => <div class="ml-8 text-size-11px opacity-70">
    <strong>{prettyBytes(usage)}</strong>
    <span>{` / ${prettyBytes(limit)} ${i18n.inUse}`}</span>
  </div>);
const FolderListHeader = memo(({
  caption,
  shared,
  tenantSharing
}: {
  caption: string;
  shared?: boolean;
  tenantSharing?: boolean;
}) => {
  const onClick = (event: Event) => {
    event.stopPropagation();
    void createFolder({
      shared: shared ? 1 : undefined,
      tenantSharing: tenantSharing ? 1 : undefined
    });
  };
  return <div class="flex items-center">
      <h4 class="flex-auto">{caption}</h4>
      <FolderButton icon={mdiFolderPlus} text={i18n.create} onClick={onClick} />
    </div>;
});
const FilenameSearchForm = memo(({
  className
}: {
  className?: string;
}) => {
  const filenameSearchInputRef = useRef<HTMLInputElement>(null);
  const query = useCurrentDoodlesQuery();
  useEffect(() => {
    if (query.kind === 'search' && filenameSearchInputRef.current) {
      filenameSearchInputRef.current.value = query.where.filename?.containsAll?.join(' ') ?? '';
    }
  }, [query]);
  const onSubmit = useCallback<preact.GenericEventHandler<HTMLFormElement>>(e => {
    e.preventDefault();
    const where: QueryWhere = {
      filename: {
        containsAll: (e.currentTarget['filename-search'] as HTMLInputElement).value.split(/\s+/)
      }
    };
    void withProgressBar(() => setCurrentDoodlesQuery({
      kind: 'search',
      where
    }));
  }, []);
  return <form class={className} onSubmit={onSubmit}>
      <label for="filename-search" class="font-size-12px">
        {i18n.filenameSearch}
      </label>
      <div class="flex items-stretch h-7 mt-0.5">
        <input ref={filenameSearchInputRef} id="filename-search" name="filename-search" class="!min-h-0 flex-1 block rounded-l border-primary border-r-0" required />
        <button class="!min-h-0 bg-white text-primary border border-primary rounded-r px-1" title={i18n.search} aria-label={i18n.search}>
          <Icon icon={mdiMagnify} size={20} />
        </button>
      </div>
    </form>;
});
const NoticeLink = memo(({
  className
}: {
  className?: string;
}) => {
  const currentUser = useCurrentUser();
  const [noticeTimestamp, setNoticeTimestamp] = useState(0);
  useEffect(() => {
    void fetch('https://genkei.co.jp/zumen/wp-json/wp/v2/posts?category_name=news&per_page=1&_fields=date_gmt').then(async response => {
      if (response.ok) {
        const [{
          date_gmt
        }] = (await response.json()) as [{
          date_gmt: string;
          title: {
            rendered: string;
          };
        }];
        date_gmt && setNoticeTimestamp(new Date(`${date_gmt}Z`).getTime());
      }
    }).catch((error: unknown) => console.warn('failed to fetch the latest news date', error));
  }, []);
  const noticeNew = !!noticeTimestamp && !currentUser?.guest && (currentUser?.visitedNoticeAt ?? 0) < noticeTimestamp;
  return <a class={cx('font-bold inline-flex items-center hover:underline', className)} rel="noreferrer" target="_blank" href="https://genkei.co.jp/zumen/archives/category/news" onClick={() => {
    setNoticeTimestamp(0);
    noticeNew && void fetch(URL_API_CURRENT_USER_VISITED_NOTICE, {
      method: 'POST'
    });
  }}>
      {i18n.notice}
      <Icon icon={mdiBellOutline} size={18} className="ml-1" />
      {noticeNew && <div class="font-size-10px w-4 h-4 rounded-full flex items-center justify-center relative -top-7px -left-9px transform scale-75 bg-red-400 text-white">
          N
        </div>}
    </a>;
});
export const FolderList = memo(() => {
  const query = useCurrentDoodlesQuery();
  const personalFolders = usePersonalFolders();
  const sharedFolders = useSharedFolders();
  const tenantFolders = useTenantFolders();
  const currentUser = useCurrentUser();
  const currentUserTenant = useCurrentUserTenant();
  const rootFolder = useRef<Folder>({
    id: '',
    name: i18n.rootFolderName,
    ownerUserId: '',
    tenantSharing: 0,
    users: {}
  });
  if (!currentUser) {
    return null;
  }
  const tenantStorageSize = currentUserTenant?.zumeco?.license?.tenantStorageSize ?? 0;
  return <section class="w-256px py-3 bg-white overflow-auto" style={`box-shadow: 0 0 2px ${COLOR_PANEL_SHADOW}`}>
      <NoticeLink className="ml-4 opacity-85" />

      <div class="border-t ml-3 mr-2 mt-2 mb-2 opacity-50" />

      <FilenameSearchForm className="pl-4 pr-2" />

      <div class="h-5" />

      <Collapsible disabled={!tenantStorageSize}>
        <FolderListHeader caption={tenantStorageSize ? i18n.personalFolders : i18n.folders} />
        <>
          {!currentUser.guest && <StorageUsage usage={currentUser.storageUsage ?? 0} limit={currentUser.storageLimit ?? 0} />}
          <ul class="p-0 pt-1 pb-4 font-size-14px xl:font-size-15px">
            {!currentUser.guest && <FolderListItem folder={rootFolder.current} active={query.kind === 'folder' && !query.folderId} />}
            {personalFolders.map(folder => <FolderListItem key={folder.id} folder={folder} active={query.kind === 'folder' && query.folderId === folder.id} />)}
          </ul>
        </>
      </Collapsible>

      <div class="h-2" />

      {!!currentUserTenant?.zumeco?.license?.maxFolderGuestCount && <>
          <Collapsible>
            <FolderListHeader caption={i18n.sharing.sharedFolder} shared />
            <ul class="p-0 pt-1 pb-4 font-size-14px xl:font-size-15px">
              {sharedFolders.map(folder => <FolderListItem key={folder.id} folder={folder} active={query.kind === 'folder' && query.folderId === folder.id} />)}
            </ul>
          </Collapsible>

          <div class="h-2" />
        </>}

      {!!tenantStorageSize && <Collapsible>
          <FolderListHeader caption={i18n.tenantFolders} tenantSharing />
          <>
            {!currentUser.guest && <StorageUsage usage={currentUser.tenantStorageUsage ?? 0} limit={tenantStorageSize} />}
            <ul class="p-0 mt-1 font-size-14px xl:font-size-15px">
              {tenantFolders.map(folder => <FolderListItem key={folder.id} folder={folder} active={query.kind === 'folder' && query.folderId === folder.id} />)}
            </ul>
          </>
        </Collapsible>}
    </section>;
});